import { FC, ReactNode } from "react"
import classNames from "classnames"
import InformativeIcons from "../InformativeIcons"
import { VitalDisplayItem } from "../utils"
import AlertLayout from "."
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import { ProcessingMethod } from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import "./index.scss"

const defaultDescription = (informativeTime: string | undefined): ReactNode => (
   <>
      <div>Threshold triggered</div>
      <div className="alert-modal__informative-time">{informativeTime}</div>
   </>
)

type DescriptionRenderer = (informativeTime: string | undefined) => ReactNode

const descriptionByConditionType: Partial<
   Record<AlertSystemConditionType, DescriptionRenderer>
> = {
   [AlertSystemConditionType.NO_VITALS]: () => (
      <div>
         No vital signs detected <br />
         Check inmate immediately
      </div>
   ),
   [AlertSystemConditionType.BROKEN_WEARABLE]: (informativeTime) => (
      <>
         <div>Alarm triggered</div>
         <div className="alert-modal__informative-time">{informativeTime}</div>
         <div className="alert-modal__informative-instruction">
            Please assign a new device to the current inmate
         </div>
      </>
   ),
}

const getDescription = (
   systemConditionType: AlertSystemConditionType | null,
   informativeTime: string | undefined
): ReactNode => {
   const renderDescription = systemConditionType
      ? descriptionByConditionType[systemConditionType]
      : undefined

   return (renderDescription ?? defaultDescription)(informativeTime)
}

interface AlertInfoProps {
   open: boolean
   className: string
   isCustom: boolean
   headerIconName: IconProps["name"]
   headerText: string
   iconColor: IconColor
   systemConditionType: AlertSystemConditionType | null
   informativeTime: string | undefined
   vitalsDisplay: VitalDisplayItem[]
   onClose: () => void
   children: ReactNode
}

const AlertInfo: FC<AlertInfoProps> = ({
   open,
   className,
   isCustom,
   headerIconName,
   headerText,
   iconColor,
   systemConditionType,
   informativeTime,
   vitalsDisplay,
   onClose,
   children,
}) => {
   return (
      <AlertLayout
         open={open}
         className={className}
         isCustom={isCustom}
         headerIconName={headerIconName}
         headerText={headerText}
         iconColor={iconColor}
         description={getDescription(systemConditionType, informativeTime)}
         informativeIcons={
            systemConditionType === AlertSystemConditionType.NO_VITALS ? (
               <div
                  className={classNames(
                     "alert-layout__informative-icons",
                     "alert-layout__informative-icons--critical"
                  )}
               >
                  <InformativeIcons
                     iconColor={iconColor}
                     informativeIcons={[
                        { iconName: "heartRate" /* , vitalValue: "-" */ },
                        { iconName: "movement" as IconProps["name"] },
                        { iconName: "respiratoryRate" /* , vitalValue: "-" */ },
                     ]}
                  />
               </div>
            ) : vitalsDisplay.length > 0 ? (
               <div className="alert-layout__informative-icons">
                  {vitalsDisplay.map((vital) => (
                     <span
                        key={vital.iconName}
                        className="alert-modal__informative-vital"
                     >
                        <span className="alert-modal__informative-vital-icon">
                           <Icon
                              name={vital.iconName}
                              size="53"
                              color={iconColor}
                           />
                        </span>
                        <span className="alert-modal__informative-vital-value">
                           <Icon
                              name={vital.arrowIcon}
                              size="38"
                              color={iconColor}
                           />
                           {`${vital.thresholdValue}${
                              vital.thresholdProcessingMethod ===
                              ProcessingMethod.DYNAMIC
                                 ? "%"
                                 : ""
                           }`}
                        </span>
                     </span>
                  ))}
               </div>
            ) : undefined
         }
         onClose={onClose}
      >
         {children}
      </AlertLayout>
   )
}

export default AlertInfo
